import { useEffect, forwardRef, useImperativeHandle, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import {
	DataGridPremium,
	useGridApiRef,
	useKeepGroupedColumnsHidden
} from '@mui/x-data-grid-premium';
import TableFilters from '../TableFilters/TableFilters';
import TableActions from '../TableActions/TableActions';
import StyledWrapper from './style';

const Table = forwardRef(
	(
		{
			rows,
			columns,
			minWidth,
			rowHeight,
			customGetRowHeight,
			columnBuffer,
			rowGroupingModel,
			rowHover,
			checkboxSelection,
			onSelectChange,
			isRowSelectable,
			keepNonExistentRowsSelected,
			customHeight,
			marginTop,
			groupingColDef,
			disableSelectionOnClick,
			treeData,
			getTreeDataPath,
			getRowId = (row) => row.id,
			rowCount,
			pageSize,
			onPageSizeChange,
			style,
			rowReordering,
			disableReorder,
			onRowOrderChange,
			disablePagination,
			paginationMode = 'client',
			onPageChange,
			page,
			loading,
			enableAutoPageSize,
			onColumnResize,
			sortingMode,
			onSortModelChange,
			setHiddenColumns,
			hideFilters,
			setOuterApiRef,
			key,
			onFilterModelChange,
			hideDisabledCheckboxes,
			addGroupDividers,
			filterModel,
			sortModel,
			hideTableActions = false,
			emailType,
			updateOrderedColumns,
			exportTableToCSV,
			exportTableToPDF,
			tableExportAvailable,
			exportReviewResponse,
			exportSubmissionResponse,
			emailEndpoint,
			disableRowGrouping = true,
			isOpenedFilter = false,
			excludeColumns = [],
			moveCheckboxColumn = false,
			rowsPerPageOptions = [],
			tableContainerRef
		},
		ref
	) => {
		const apiRef = useGridApiRef();
		const [selectionModel, setSelectionModel] = useState([]);
		const [selectedRows, setSelectedRows] = useState([]);
		const [previouslySelectedRowIDs, setPreviouslySelectedRowIDs] = useState([]);
		const [finalColumns, setFinalColumns] = useState(columns);
		const [visibleColumns, setVisibleColumns] = useState([]);

		useImperativeHandle(ref, () => ({
			clearSelection: () => {
				setSelectionModel([]);
				setSelectedRows([]);
				setPreviouslySelectedRowIDs([]);

				onSelectChange && onSelectChange([]);
			}
		}));

		const customStyle = {
			'& .MuiDataGrid-cell:focus': {
				outline: 'none'
			},
			'& .MuiDataGrid-cell:focus-within': {
				outline: 'none'
			},
			'& .MuiDataGrid-groupingCriteriaCellToggle': {
				marginRight: '0px'
			},
			'& .groupedRow': {
				backgroundColor: 'rgba(217, 217, 217, 0.26)'
			},
			'& .borderedRow': {
				borderTop: '1px solid rgba(0,0,0,0.5)'
			},
			'& .groupedStart': {
				borderTop: '1px solid rgba(0, 0, 0, 0.54)'
			},
			'& .MuiDataGrid-columnHeader[aria-label="Group"]': {
				display: 'none !important'
			},
			'& .MuiDataGrid-cell[data-field="__tree_data_group__"]': {
				padding: '0 !important'
			},
			'& .MuiDataGrid-cellCheckbox .Mui-disabled': {
				display: hideDisabledCheckboxes ? 'none !important' : 'block'
			}
		};

		const handleFilterChange = useCallback(
			(newFilterOptions) => {
				if (onFilterModelChange) onFilterModelChange(newFilterOptions?.items ?? []);
			},
			[onFilterModelChange]
		);

		const handleSortChange = useCallback(
			(newSortOptions) => {
				if (onSortModelChange) onSortModelChange(newSortOptions);
			},
			[onSortModelChange]
		);

		useEffect(() => {
			hideColumns(setHiddenColumns);
		}, [setHiddenColumns]);

		useEffect(() => {
			setOuterApiRef && setOuterApiRef(apiRef);
			setVisibleColumns(
				apiRef?.current
					?.getVisibleColumns()
					?.map((e) => e?.field)
					.filter((column) => !['__check__', '__tree_data_group__'].includes(column)) ?? []
			);

			if (moveCheckboxColumn) {
				apiRef?.current?.setColumnIndex('__check__', 1);
			}
		}, [apiRef]);

		const onRowClick = useCallback(
			(params) => {
				const rowNode = apiRef.current.getRowNode(params.id);
				if (rowNode && rowNode.isAutoGenerated) {
					apiRef.current.setRowChildrenExpansion(params.id, !rowNode.childrenExpanded);
				}
			},
			[apiRef]
		);

		const initialState = useKeepGroupedColumnsHidden({
			apiRef,
			rowGroupingModel
		});

		const onRowHover = (e) => {
			const rowId = Number(e.currentTarget.dataset.id);
			rowHover && rowHover(rowId);
		};

		const hideColumns = (hiddenColumns = []) => {
			apiRef.current
				.getAllColumns()
				.map((c) => c.field)
				.forEach((c) => {
					const isVisible = !hiddenColumns.includes(c);
					apiRef.current.setColumnVisibility(c, isVisible);
				});
		};

		const handleFilterSave = (filter) => {
			onFilterModelChange && onFilterModelChange(filter);
		};

		const resetColumnFilters = () => {
			onFilterModelChange && onFilterModelChange([]);
			onSortModelChange && onSortModelChange([]);
			columns.forEach((colume) => apiRef.current.setColumnVisibility(colume.field, true));
		};

		const getRowHeight = () => (!rowHeight ? 'auto' : 52);

		const handleSelectionModelChange = (currentSelectedRowIDs) => {
			setSelectionModel(currentSelectedRowIDs);
			const currentSelectedRows = [...selectedRows];

			if (previouslySelectedRowIDs.length === 0) {
				// If no previously selected rows, add all selected rows to currentSelectedRows
				currentSelectedRowIDs.forEach((rowID) => {
					const targetRow = rows.find((row) => getRowId(row) === rowID);
					currentSelectedRows.push(targetRow);
				});
			} else {
				// First we need to check previously selected rows to see if target row has not been added yet and add it or needs to be removed
				previouslySelectedRowIDs.forEach((rowID) => {
					const targetRowAlreadySelected =
						currentSelectedRows.findIndex((row) => getRowId(row) === rowID) !== -1;

					if (!targetRowAlreadySelected) {
						// Add the row if not yet selected
						const targetRow = rows.find((row) => getRowId(row) === rowID);
						currentSelectedRows.push(targetRow);
					} else {
						// Only remove the row if it is no longer selected, otherwise leave it alone
						if (!currentSelectedRowIDs.includes(rowID)) {
							const targetRowIndex = currentSelectedRows.findIndex(
								(row) => getRowId(row) === rowID
							);
							currentSelectedRows.splice(targetRowIndex, 1);
						}
					}
				});

				// Then we need to add all newly selected rows
				const newlySelectedRowIDs = currentSelectedRowIDs.filter(
					(rowID) => !previouslySelectedRowIDs.includes(rowID)
				);

				newlySelectedRowIDs.forEach((rowID) => {
					const targetRow = rows.find((row) => getRowId(row) === rowID);
					currentSelectedRows.push(targetRow);
				});
			}

			// Saving current selected row IDs to state so that on next selection or deselection we know exactly what has changed by comparing previously selected IDs
			// to current selected IDs and can act accordingly (the callback argument for onSelectionModelChange does not provide any information about which exact item
			// was selected or deselected)
			setPreviouslySelectedRowIDs(currentSelectedRowIDs);

			// Saving selected rows to state so that we have a list that persists across pages
			setSelectedRows(currentSelectedRows);

			onSelectChange && onSelectChange(currentSelectedRows);
		};

		return (
			<Box
				sx={{
					height: customHeight || `80vh`,
					minHeight: `400px`,
					width: '100%',
					minWidth,
					display: 'flex',
					marginTop: marginTop ?? '30px'
				}}
				key={key}
				ref={tableContainerRef}
				className={disableReorder && 'disableTableRows'}
			>
				<DataGridPremium
					disableColumnFilter
					disableColumnSelector
					disableRowGrouping={disableRowGrouping}
					initialState={{
						...initialState,
						sorting: {
							sortModel: sortModel ?? []
						},
						filter: {
							filterModel: filterModel ?? []
						}
					}}
					sortingMode={sortingMode}
					onSortModelChange={handleSortChange}
					onColumnResize={onColumnResize}
					checkboxSelection={checkboxSelection}
					onSelectionModelChange={handleSelectionModelChange}
					selectionModel={selectionModel}
					sortModel={sortModel}
					defaultGroupingExpansionDepth={-1}
					groupingColDef={groupingColDef}
					isRowSelectable={isRowSelectable}
					keepNonExistentRowsSelected={keepNonExistentRowsSelected}
					apiRef={apiRef}
					onRowClick={onRowClick}
					rows={rows}
					filterModel={{
						items: filterModel ?? [],
						linkOperator: 'and',
						quickFilterLogicOperator: 'and',
						quickFilterValues: []
					}}
					onFilterModelChange={handleFilterChange}
					filterMode={onFilterModelChange ? 'server' : 'client'}
					rowReordering={rowReordering}
					onRowOrderChange={onRowOrderChange}
					rowCount={rowCount}
					getRowId={getRowId}
					columns={finalColumns}
					disableSelectionOnClick={disableSelectionOnClick}
					rowGroupingModel={rowGroupingModel}
					experimentalFeatures={{ newEditingApi: true }}
					pagination={!disablePagination}
					autoPageSize={enableAutoPageSize}
					columnBuffer={columnBuffer || 1000}
					style={{ ...style, border: '0px' }}
					getRowHeight={customGetRowHeight ? customGetRowHeight : getRowHeight}
					sx={customStyle}
					componentsProps={{
						row: {
							onMouseEnter: (e) => onRowHover(e) // <------- Here
						}
					}}
					treeData={treeData}
					getTreeDataPath={getTreeDataPath}
					pageSize={pageSize}
					onPageSizeChange={onPageSizeChange}
					rowsPerPageOptions={rowsPerPageOptions}
					paginationMode={paginationMode}
					onPageChange={onPageChange}
					page={page}
					loading={loading}
					getRowClassName={(params) => {
						if (params?.row?.hierarchy?.length === 1 && addGroupDividers) {
							return 'groupedStart';
						}
						if (params?.row?.type?.includes('Application Instance')) {
							return 'groupedRow';
						}
						if (
							!params.row?.status &&
							(params.row?.type?.includes('APPLICATION PROCESS') ||
								params.row?.type?.includes('Submission Stage') ||
								params.row?.type?.includes('Evaluation Stage'))
						) {
							return 'borderedRow';
						}
					}}
					onColumnVisibilityModelChange={(_) => {
						setVisibleColumns(
							apiRef?.current
								?.getVisibleColumns()
								?.map((e) => e?.field)
								.filter((column) => !['__check__', '__tree_data_group__'].includes(column))
						);
					}}
				/>

				<StyledWrapper.ActionsContainer>
					{!hideTableActions && (
						<TableActions
							activeFilters={filterModel}
							emailType={emailType}
							tableExportAvailable={tableExportAvailable}
							exportTableToCSV={exportTableToCSV}
							exportTableToPDF={exportTableToPDF}
							exportReviewResponse={exportReviewResponse}
							exportSubmissionResponse={exportSubmissionResponse}
							emailEndpoint={emailEndpoint}
							rowCount={rowCount}
						/>
					)}
					{!hideFilters && (
						<TableFilters
							handleFilterSave={handleFilterSave}
							resetColumnFilters={resetColumnFilters}
							updateOrderedColumns={updateOrderedColumns}
							apiRef={apiRef}
							columns={finalColumns.filter((column) => !excludeColumns.includes(column.field))}
							allColumns={finalColumns}
							setColumns={setFinalColumns}
							activeFilters={filterModel}
							visibleColumns={visibleColumns}
							isOpened={isOpenedFilter}
						/>
					)}
				</StyledWrapper.ActionsContainer>
			</Box>
		);
	}
);

export default Table;
